// src/components/ui/ConfirmDialog.tsx
/**
 * ConfirmDialog — reemplazo de window.confirm() con el modal del sistema
 * de diseño (tq95, fix 2026-09-20).
 *
 * Misma arquitectura que showToast()/ToastContainer (src/components/ui/Toast.tsx):
 * singleton por event-bus, un solo container montado una vez (Layout.tsx,
 * junto a <ToastContainer />). Mismo shell visual que FeedbackModal.tsx
 * (backdrop + card rounded-2xl + header/body/footer).
 *
 * Uso — reemplaza 1 a 1 el patrón `if (!window.confirm(msg)) return;`:
 *   if (!(await confirmDialog(msg))) return;
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { colors } from '../../utils/theme';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' = botón de confirmar en rojo (borrar/anular). Default: 'default'. */
  variant?: 'default' | 'danger';
}

// ── Singleton event bus (mismo patrón que showToast) ──────────────
// Un solo resolver activo a la vez — el caller siempre hace
// `await confirmDialog(...)`, nunca concurrente. Si por algún motivo un
// segundo confirmDialog() se dispara mientras el primero sigue pendiente,
// el primero se resuelve `false` (cancelado) en vez de quedar colgado.
type ConfirmListener = (pending: ConfirmOptions) => void;
let _listener: ConfirmListener | null = null;
let _resolve: ((value: boolean) => void) | null = null;

/** Reemplazo de window.confirm() — muestra el modal del sistema de diseño y resuelve true/false. */
export function confirmDialog(message: string, options: Omit<ConfirmOptions, 'message'> = {}): Promise<boolean> {
  _resolve?.(false); // resuelve cualquier confirm previo sin cerrar, evita promesas colgadas
  return new Promise(resolve => {
    _resolve = resolve;
    if (_listener) {
      _listener({ message, ...options });
    } else {
      // Sin container montado (no debería pasar en producción — se monta en
      // Layout.tsx) — falla cerrado, igual que window.confirm() cancelado.
      _resolve = null;
      resolve(false);
    }
  });
}

// ── Container (mount once en Layout.tsx) ───────────────────────────
export function ConfirmDialogContainer() {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);

  useEffect(() => {
    _listener = (p) => setPending(p);
    return () => { _listener = null; };
  }, []);

  const resolve = (value: boolean) => {
    setPending(null);
    _resolve?.(value);
    _resolve = null;
  };

  if (!pending) return null;

  const isDanger = pending.variant === 'danger';
  const Icon = isDanger ? AlertTriangle : HelpCircle;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => resolve(false)}
    >
      <div
        className="w-full max-w-[420px] rounded-2xl overflow-hidden shadow-[0_24px_60px_color-mix(in srgb, var(--canvas) 60%, transparent),0_0_0_1px_var(--brand-soft)]"
        style={{ background: c.modal, border: `1px solid ${c.border}` }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={
              isDanger
                ? { background: 'var(--surface-raised)', border: '1px solid var(--surface-raised)' }
                : { background: 'var(--brand-soft)', border: '1px solid var(--brand-soft)' }
            }
          >
            <Icon size={18} style={{ color: isDanger ? c.negative : 'var(--brand)' }} />
          </div>
          <div className="flex-1 min-w-0 pt-1.5">
            {pending.title && (
              <div className="t-h3 font-bold" style={{ color: c.textPrimary }}>{pending.title}</div>
            )}
            <p className="t-sm leading-relaxed whitespace-pre-line" style={{ color: c.textNormal, marginTop: pending.title ? 4 : 0 }}>
              {pending.message}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => resolve(false)}
            className="px-4 py-2 rounded-lg t-sm font-semibold transition-colors"
            style={{ color: c.textNormal, background: c.subtleBg, border: `1px solid ${c.border}` }}
          >
            {pending.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            type="button"
            onClick={() => resolve(true)}
            className="px-4 py-2 rounded-lg t-sm font-semibold text-white transition-colors"
            style={
              isDanger
                ? { background: c.negative, color: 'var(--surface)' }
                : { background: 'var(--brand)', color: 'var(--on-brand)' }
            }
          >
            {pending.confirmLabel ?? 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
